import type { InvoiceStatus, User } from 'payments-api/model';
import React from 'react';

import { type CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { PrimaryButtonV3 } from '@fragment/ui/src/components/Button/PrimaryButtonV3/PrimaryButtonV3';
import { SecondaryButtonV3 } from '@fragment/ui/src/components/Button/SecondaryButtonV3/SecondaryButtonV3';
import { TertiaryButton } from '@fragment/ui/src/components/Button/TertiaryButton/TertiaryButton';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { CurrencyField } from '@fragment/ui/src/components/Payments/CurrencyField';
import {
  LayoutCell,
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import { StatusBadge } from '@fragment/ui/src/components/Payments/StatusBadge';

type Action = {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTooltipText?: string;
};

const kActionButtonWidth =
  'w-full text-sm largeLaptop:w-f8 h-full largeLaptop:text-md';
const ActionButtons = ({
  primaryAction,
  secondaryAction,
}: {
  primaryAction?: Action;
  secondaryAction?: Action;
}) => (
  <div
    className="flex flex-row gap-x-f1 w-fit h-full justify-between"
    data-testid="invoice-action-buttons"
  >
    {secondaryAction && (
      <SecondaryButtonV3
        onClick={secondaryAction.onClick}
        className={kActionButtonWidth}
        disabled={secondaryAction.disabled}
        disabledTooltipText={secondaryAction.disabledTooltipText}
      >
        {secondaryAction.title}
      </SecondaryButtonV3>
    )}
    {primaryAction && (
      <PrimaryButtonV3
        onClick={primaryAction.onClick}
        className={kActionButtonWidth}
        disabled={primaryAction.disabled}
        disabledTooltipText={primaryAction.disabledTooltipText}
      >
        {primaryAction.title}
      </PrimaryButtonV3>
    )}
  </div>
);

type Props = {
  onBack: () => void;
  title: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  status: InvoiceStatus;
  totalAmount: {
    amount: number;
    currency: CurrencyCode;
  }[];
  issued: string;
  buyer: string;
  parties?: User[];
  setBuyer?: (buyer: string) => void;
  setTitle?: (title: string) => void;
  children: React.ReactNode;
};

export const InvoiceLayout = ({
  onBack,
  title,
  primaryAction,
  secondaryAction,
  status,
  totalAmount,
  issued,
  buyer,
  parties,
  setBuyer,
  setTitle,
  children,
}: Props) => (
  <div
    className="flex flex-col w-full h-fit min-h-[100dvh] font-sans py-f2"
    data-testid="invoice-layout"
  >
    <LayoutRow isFirst height="medium">
      <LayoutCell border="none" align="top">
        <TertiaryButton onClick={onBack}>
          <Icon type="left" size="md" primaryColor="gray" />
        </TertiaryButton>
      </LayoutCell>
      <LayoutCell border="left" width={secondaryAction ? 'standard' : 'half'}>
        <ActionButtons
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
        />
      </LayoutCell>
    </LayoutRow>

    {/* start title section */}
    <LayoutRow height="large">
      <TextCell
        text={title}
        textSize="lg"
        align="left"
        border="none"
        onChange={setTitle ? (text) => setTitle(text) : undefined}
        fullWidth
        data-testid="invoice-title-input"
      />
      <LayoutCell border="left">
        <StatusBadge status={status as 'draft' | 'closed' | 'open'} />
      </LayoutCell>
    </LayoutRow>

    {/* start detail section */}

    <LayoutRow height="small" justify="start">
      <TextCell
        text="Buyer"
        textSize="md"
        align="left"
        border="right"
        secondary
      />
      {setBuyer && parties ? (
        <div className="flex h-full p-f1 w-full">
          <Dropdown
            options={parties.map((user) => ({
              value: user.externalId,
              label: user.externalId,
            }))}
            displayValue={buyer}
            onChange={setBuyer}
            placeholder="Select"
            data-testid="buyer-party-dropdown"
            size="md"
            width="w-full"
            height="h-full"
          />
        </div>
      ) : (
        <TextCell
          text={buyer}
          textSize="md"
          align="left"
          border="none"
          fullWidth
          data-testid="buyer-party-text"
        />
      )}
    </LayoutRow>
    <LayoutRow height="small" justify="start">
      <TextCell
        text="Issued"
        textSize="md"
        align="left"
        border="right"
        secondary
      />
      <TextCell
        text={new Date(issued).toDateString().split(' ').slice(1).join(' ')}
        textSize="md"
        align="left"
        border="none"
        useMonoText
      />
    </LayoutRow>
    <LayoutRow height="medium" justify="start">
      <TextCell
        text="Amount"
        textSize="md"
        align="left"
        border="right"
        secondary
      />
      <LayoutCell border="none" align="top" width="full">
        {totalAmount.map((amount) => (
          <CurrencyField
            key={amount.currency}
            amount={amount.amount}
            currencyPrecision={
              currencyPrecisionMap[amount.currency as CurrencyCode]
            }
            currencyCode={amount.currency as CurrencyCode}
            textSize="md"
            align="left"
          />
        ))}
      </LayoutCell>
    </LayoutRow>
    {children}
  </div>
);
